import { ComponentType } from 'react';

import { BillToAddressCard, CompanyCard, SoldToAddressCard } from '../../../../../components/cards/company';
import { FinancialAttachmentCard, PayerAddressCard } from '../../../../../components/cards/financial';
import { AdditionalAttachmentCard } from '../../../../../components/cards/financial/AttachmentCard';
import PrivacyPolicyCard from '../../../../../components/cards/financial/PrivacyPolicyCard';
import {
  CertOfAnalysisContactCard,
  DeliveryInstructionsCard,
  InvoiceDeliveryContactCard,
  OrderConfirmationContactCard,
  ShipToAddressCard
} from '../../../../../components/cards/shipTo';
import CompanyPage from '../../../../../features/CompanyPage';
import FinancialPage from '../../../../../features/FinancialPage';
import ReviewPage from '../../../../../features/ReviewPage';
import ShipToPage from '../../../../../features/ShipTo';
import { CARD_CATEGORIES } from '../../../../../shared/cardConfig/constants';
import {
  ATTACHMENT_FIELDS,
  BILL_TO_ADDRESS_FIELDS,
  COMPANY_FIELDS,
  CONTACT_FIELDS,
  CONTACT_SAME_AS_FIELDS,
  DELIVERY_INSTRUCTIONS_FIELDS,
  PAYER_ADDRESS_FIELDS,
  SHIP_TO_ADDRESS_FIELDS,
  SOLD_TO_ADDRESS_FIELDS
} from '../../../../../shared/fieldConfig/constants';
import { PAGE_ROUTES } from '../../../../../utils/constants';
import { getShipToSubtitle } from '../../../../../utils/hooksLib';
import billToPages from './billTo';
import payerPages from './payer';
import shipToPages from './shipTo';
import soldToPages from './soldTo';

// ----------------- Type Definitions -----------------
interface Field {
  key: string;
  required?: boolean;
}

interface Card {
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  fields?: Field[];
  fieldWrapper?: string;
  sameAsFields?: string[];
}

interface Page {
  route: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  cards?: Card[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  accordions?: Array<{ title: string; cards: Card[] } | ((args: any) => any)>;
  validateFinancials?: boolean;
}

// ******* PAGE AND CARD CONFIGS **********
const companyPageTemplate: Page = {
  route: PAGE_ROUTES.company,
  Component: CompanyPage,
  cards: [
    {
      key: CARD_CATEGORIES.company,
      Component: CompanyCard,
      fields: [
        { key: COMPANY_FIELDS.legalName, required: true },
        { key: COMPANY_FIELDS.tradeDbaName, required: true }
      ]
    },
    {
      key: CARD_CATEGORIES.soldTo,
      Component: SoldToAddressCard,
      fields: [
        { key: SOLD_TO_ADDRESS_FIELDS.address },
        { key: SOLD_TO_ADDRESS_FIELDS.taxNumber1, required: true },
        { key: SOLD_TO_ADDRESS_FIELDS.taxNumber2 },
        { key: SOLD_TO_ADDRESS_FIELDS.taxNumber3 },
        { key: SOLD_TO_ADDRESS_FIELDS.taxNumber4 },
        { key: SOLD_TO_ADDRESS_FIELDS.email },
        { key: SOLD_TO_ADDRESS_FIELDS.telephone1 },
        { key: SOLD_TO_ADDRESS_FIELDS.telephone2 }
      ]
    },
    {
      key: CARD_CATEGORIES.billTo,
      Component: BillToAddressCard,
      fields: [
        { key: BILL_TO_ADDRESS_FIELDS.address },
        { key: BILL_TO_ADDRESS_FIELDS.taxNumber1 },
        { key: BILL_TO_ADDRESS_FIELDS.taxNumber2 },
        { key: BILL_TO_ADDRESS_FIELDS.taxNumber3 },
        { key: BILL_TO_ADDRESS_FIELDS.taxNumber4 }
      ]
    }
  ]
};

const contactFields: Field[] = [{ key: CONTACT_FIELDS.email }, { key: CONTACT_FIELDS.fax }];

const shipToPageTemplate: Page = {
  route: PAGE_ROUTES.shipTo,
  Component: ShipToPage,
  cards: [
    {
      key: CARD_CATEGORIES.address,
      Component: ShipToAddressCard,
      fields: [
        { key: SHIP_TO_ADDRESS_FIELDS.address },
        { key: SHIP_TO_ADDRESS_FIELDS.taxNumber1 },
        { key: SHIP_TO_ADDRESS_FIELDS.taxNumber2 },
        { key: SHIP_TO_ADDRESS_FIELDS.taxNumber3 },
        { key: SHIP_TO_ADDRESS_FIELDS.taxNumber4 },
        { key: SHIP_TO_ADDRESS_FIELDS.email },
        { key: SHIP_TO_ADDRESS_FIELDS.telephone1 },
        { key: SHIP_TO_ADDRESS_FIELDS.telephone2 }
      ],
      fieldWrapper: CARD_CATEGORIES.shipTo
    },
    {
      key: CARD_CATEGORIES.deliveryInstructions,
      Component: DeliveryInstructionsCard,
      fields: [
        { key: DELIVERY_INSTRUCTIONS_FIELDS.shippingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.externalInvoiceText },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.loadingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.unloadingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.packingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.billOfLadingText }
      ],
      fieldWrapper: CARD_CATEGORIES.shipTo
    },
    {
      key: CARD_CATEGORIES.invoiceDelivery,
      Component: InvoiceDeliveryContactCard,
      fields: [...contactFields],
      fieldWrapper: CARD_CATEGORIES.shipTo
    },
    {
      key: CARD_CATEGORIES.coa,
      Component: CertOfAnalysisContactCard,
      fields: contactFields,
      fieldWrapper: CARD_CATEGORIES.shipTo,
      sameAsFields: [CONTACT_SAME_AS_FIELDS.sameAsInvoiceDelivery]
    },
    {
      key: CARD_CATEGORIES.orderConfirmation,
      Component: OrderConfirmationContactCard,
      fields: contactFields,
      fieldWrapper: CARD_CATEGORIES.shipTo,
      sameAsFields: [CONTACT_SAME_AS_FIELDS.sameAsCoa]
    }
  ]
};

const financialPageTemplate: Page = {
  route: PAGE_ROUTES.financial,
  Component: FinancialPage,
  validateFinancials: true,
  cards: [
    {
      key: CARD_CATEGORIES.payer,
      Component: PayerAddressCard,
      fields: [
        { key: PAYER_ADDRESS_FIELDS.address },
        { key: PAYER_ADDRESS_FIELDS.taxNumber1, required: true },
        { key: PAYER_ADDRESS_FIELDS.taxNumber2 },
        { key: PAYER_ADDRESS_FIELDS.taxNumber3 },
        { key: PAYER_ADDRESS_FIELDS.taxNumber4 },
        { key: PAYER_ADDRESS_FIELDS.email },
        { key: PAYER_ADDRESS_FIELDS.telephone1 },
        { key: PAYER_ADDRESS_FIELDS.telephone2 }
      ]
    },
    {
      key: CARD_CATEGORIES.financialAttachmentCard,
      Component: FinancialAttachmentCard,
      fields: [{ key: ATTACHMENT_FIELDS.financialCardAttachment }]
    },
    {
      key: CARD_CATEGORIES.additionalAttachmentCard,
      Component: AdditionalAttachmentCard,
      fields: [{ key: ATTACHMENT_FIELDS.additionalAttachment }]
    },
    {
      key: CARD_CATEGORIES.privacyPolicy,
      Component: PrivacyPolicyCard
    }
  ]
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getShipToCardsForReviewPage({ store }: { store: any }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return store.customer.shipTo.map((shipTo: any, i: number) => ({
    title: `tabTitles.ship to`,
    count: i + 1,
    subtitle: getShipToSubtitle({ shipToAddress: shipTo.address, store }),
    cards:
      i === 0
        ? shipToPageTemplate.cards
        : shipToPageTemplate.cards!.filter(({ key }) => CARD_CATEGORIES.invoiceDelivery !== key)
  }));
}

const reviewPageTemplate: Page = {
  route: PAGE_ROUTES.review,
  Component: ReviewPage,
  accordions: [
    {
      title: 'tabTitles.company',
      cards: companyPageTemplate.cards!
    },
    getShipToCardsForReviewPage,
    {
      title: 'tabTitles.financial',
      cards: [...financialPageTemplate.cards!]
    }
  ]
};

// *********** TEMPLATE ************

export const templateMap = {
  customer_set_up: {
    pages: [companyPageTemplate, shipToPageTemplate, financialPageTemplate, reviewPageTemplate]
  },
  bill_to: {
    pages: billToPages
  },
  payer: {
    pages: payerPages
  },
  ship_to: {
    pages: shipToPages
  },
  sold_to: {
    pages: soldToPages
  }
};
